import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Home, Bed, Bath, Sofa, MapPin, PoundSterling,
  Sparkles, Loader2, Copy, ClipboardCheck, Plus, X,
  AlertTriangle, ShieldCheck, FileText, Building2, Trees,
  Sun, Car, Flame, Snowflake, Wifi, ChefHat, Wrench, Star,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/AuthContext";
import { AppHeader } from "./AppHeader";
import "@/PropertyDescription.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Quick-add chips for the most common UK property features. Click to toggle in/out of the brief.
const FEATURE_CHIPS = [
  { label: "South-facing garden", icon: Sun },
  { label: "Off-street parking", icon: Car },
  { label: "Garage", icon: Car },
  { label: "New boiler", icon: Flame },
  { label: "Underfloor heating", icon: Flame },
  { label: "Air conditioning", icon: Snowflake },
  { label: "Newly fitted kitchen", icon: ChefHat },
  { label: "En-suite to master", icon: Bath },
  { label: "Family bathroom", icon: Bath },
  { label: "Downstairs WC", icon: Bath },
  { label: "Open-plan living", icon: Sofa },
  { label: "Log burner", icon: Flame },
  { label: "Recently renovated", icon: Wrench },
  { label: "Period features", icon: Star },
  { label: "Fibre broadband", icon: Wifi },
  { label: "Mature garden", icon: Trees },
  { label: "Patio / decking", icon: Trees },
  { label: "Conservatory", icon: Home },
  { label: "Utility room", icon: Wrench },
  { label: "Walking distance to station", icon: MapPin },
];

const PROPERTY_TYPES = [
  "Detached", "Semi-detached", "Terraced", "End-of-terrace",
  "Apartment", "Maisonette", "Bungalow", "Cottage", "Townhouse",
];
const TENURES = ["Freehold", "Leasehold", "Share of Freehold"];
const LENGTHS = [
  { key: "short", label: "Short", hint: "~140 words" },
  { key: "standard", label: "Standard", hint: "~220 words" },
  { key: "long", label: "Long", hint: "~320 words" },
];
const VARIANTS = [
  { key: "feature", label: "Feature-led", hint: "Lead with rooms & finishes" },
  { key: "lifestyle", label: "Lifestyle-led", hint: "How it feels to live in" },
  { key: "location", label: "Location-led", hint: "Lead with the neighbourhood" },
];

export default function PropertyDescriptionBuilder({ onBack }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  // Inputs
  const [wmsOrderId, setWmsOrderId] = useState("");
  const [address, setAddress] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");
  const [receptions, setReceptions] = useState("");
  const [tenure, setTenure] = useState("");
  const [askingPrice, setAskingPrice] = useState("");
  const [features, setFeatures] = useState([]);
  const [customFeature, setCustomFeature] = useState("");
  const [locationNotes, setLocationNotes] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");

  // Output controls
  const [length, setLength] = useState("standard");
  const [variant, setVariant] = useState("feature");

  // Super admin: pick a branch's personality to QA with
  const [branches, setBranches] = useState([]);
  const [previewBranchId, setPreviewBranchId] = useState("");

  useEffect(() => {
    if (!isSuperAdmin) return;
    axios.get(`${API}/admin/branches`).then((res) => {
      setBranches(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, [isSuperAdmin]);

  // State
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [importingWms, setImportingWms] = useState(false);

  const handleImportFromWms = async () => {
    const id = wmsOrderId.trim();
    if (!id) {
      toast.error("Enter a WMS Job ID first");
      return;
    }
    setImportingWms(true);
    try {
      const { data } = await axios.get(`${API}/property-description/wms-import/${id}`);
      const filled = [];
      if (data.address) { setAddress(data.address); filled.push("address"); }
      if (data.property_type) {
        // Only set if it matches one of our known types (case-insensitive)
        const match = PROPERTY_TYPES.find(
          (t) => t.toLowerCase() === String(data.property_type).toLowerCase()
        );
        if (match) { setPropertyType(match); filled.push("type"); }
      }
      if (data.bedrooms != null && data.bedrooms !== "") {
        setBedrooms(String(data.bedrooms));
        filled.push("beds");
      }
      if (data.bathrooms != null && data.bathrooms !== "") {
        setBathrooms(String(data.bathrooms));
        filled.push("baths");
      }
      if (data.reception_rooms != null && data.reception_rooms !== "") {
        setReceptions(String(data.reception_rooms));
        filled.push("receptions");
      }
      if (filled.length === 0) {
        toast.info("Order found, but WMS has no property details yet for this job.");
      } else {
        toast.success(`Imported from WMS: ${filled.join(", ")}`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not find that WMS order";
      toast.error(msg);
    } finally {
      setImportingWms(false);
    }
  };

  const toggleFeature = (label) => {
    setFeatures((prev) =>
      prev.includes(label) ? prev.filter((f) => f !== label) : [...prev, label]
    );
  };
  const addCustomFeature = () => {
    const t = customFeature.trim();
    if (!t) return;
    if (features.includes(t)) {
      setCustomFeature("");
      return;
    }
    setFeatures((prev) => [...prev, t]);
    setCustomFeature("");
  };
  const removeFeature = (label) => setFeatures((prev) => prev.filter((f) => f !== label));

  const copyText = async (key, text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1400);
    } catch {
      toast.error("Couldn't copy — your browser blocked it");
    }
  };

  const copyFull = () => {
    if (!result) return;
    const lines = [];
    if (result.hook) lines.push(result.hook, "");
    if (result.bullets?.length) {
      lines.push(...result.bullets.map((b) => `• ${b}`), "");
    }
    if (result.body) lines.push(result.body);
    copyText("full", lines.join("\n").trim());
  };

  const generate = async () => {
    if (!address.trim() && features.length === 0 && !wmsOrderId.trim()) {
      toast.error("Add an address, a WMS Job ID, or at least one feature");
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const payload = {
        wms_order_id: wmsOrderId.trim() ? Number(wmsOrderId.trim()) : null,
        inputs: {
          address: address.trim(),
          property_type: propertyType,
          bedrooms: bedrooms ? Number(bedrooms) : null,
          bathrooms: bathrooms ? Number(bathrooms) : null,
          reception_rooms: receptions ? Number(receptions) : null,
          tenure,
          asking_price: askingPrice.trim(),
          key_features: features,
          location_notes: locationNotes.trim(),
          additional_notes: additionalNotes.trim(),
        },
        length,
        style_variant: variant,
        branch_id_override: isSuperAdmin && previewBranchId ? previewBranchId : null,
      };
      const { data } = await axios.post(`${API}/property-description/generate`, payload);
      setResult(data);
      const cplCount = (data.compliance_notes || []).length;
      toast.success(
        cplCount > 0
          ? `Generated — ${cplCount} compliance note${cplCount === 1 ? "" : "s"} to review`
          : "Generated — CPR/BPR clean ✓"
      );
    } catch (err) {
      const msg = err.response?.data?.detail || "Generation failed";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="App" data-testid="property-description-page">
      <Toaster position="top-right" richColors />
      <div className="container">
        <AppHeader onBack={onBack} />

        <div className="pd-title-bar">
          <div className="pd-title-icon"><FileText size={20} /></div>
          <div className="pd-title-text">
            <h1 className="pd-title">Property Description</h1>
            <p className="pd-subtitle">
              AI-written listing copy in your branch tone, with CPR / BPR compliance baked in.
            </p>
          </div>
          <div className="pd-compliance-pill" data-testid="pd-compliance-pill">
            <ShieldCheck size={13} /> CPR &amp; BPR safety net
          </div>
        </div>

        <div className="pd-grid">
          {/* ─── LEFT: BRIEF ─────────────────────────────────────────── */}
          <div className="pd-card pd-brief">
            <div className="pd-section-head">
              <span className="pd-section-num">1</span>
              <h2>The brief</h2>
            </div>

            <div className="pd-row">
              <Label className="pd-label">WMS Job ID (optional)</Label>
              <div className="pd-wms-row">
                <Input
                  placeholder="e.g. 1437056 — pulls address &amp; beds automatically"
                  value={wmsOrderId}
                  onChange={(e) => setWmsOrderId(e.target.value.replace(/[^\d]/g, ""))}
                  className="pd-input pd-wms-input"
                  data-testid="pd-wms-id"
                />
                <button
                  type="button"
                  className="pd-wms-import"
                  onClick={handleImportFromWms}
                  disabled={!wmsOrderId.trim() || importingWms}
                  data-testid="pd-wms-import-btn"
                >
                  {importingWms ? (
                    <><Loader2 size={14} className="pd-spin" /> Importing</>
                  ) : (
                    <><Building2 size={14} /> Import</>
                  )}
                </button>
              </div>
              <p className="pd-hint">
                Tap Import to pull the address, beds, baths and property type from WMS.
                You can still edit anything before generating.
              </p>
            </div>

            <div className="pd-row">
              <Label className="pd-label">Property address</Label>
              <Input
                placeholder="12 High Street, Wroughton, Swindon"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="pd-input"
                data-testid="pd-address"
              />
            </div>

            <div className="pd-row pd-row-split">
              <div>
                <Label className="pd-label">Property type</Label>
                <div className="pd-chip-row">
                  {PROPERTY_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`pd-pill ${propertyType === t ? "is-active" : ""}`}
                      onClick={() => setPropertyType(propertyType === t ? "" : t)}
                      data-testid={`pd-type-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="pd-row pd-numbers">
              <div>
                <Label className="pd-label"><Bed size={14} /> Beds</Label>
                <Input type="number" value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} className="pd-input pd-num" data-testid="pd-beds" />
              </div>
              <div>
                <Label className="pd-label"><Bath size={14} /> Baths</Label>
                <Input type="number" value={bathrooms} onChange={(e) => setBathrooms(e.target.value)} className="pd-input pd-num" data-testid="pd-baths" />
              </div>
              <div>
                <Label className="pd-label"><Sofa size={14} /> Receptions</Label>
                <Input type="number" value={receptions} onChange={(e) => setReceptions(e.target.value)} className="pd-input pd-num" data-testid="pd-rec" />
              </div>
              <div>
                <Label className="pd-label">Tenure</Label>
                <div className="pd-chip-row pd-chip-row-tight">
                  {TENURES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`pd-pill pd-pill-sm ${tenure === t ? "is-active" : ""}`}
                      onClick={() => setTenure(tenure === t ? "" : t)}
                      data-testid={`pd-tenure-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="pd-row">
              <Label className="pd-label"><PoundSterling size={14} /> Asking price</Label>
              <Input
                placeholder="£375,000 or guide £350,000–£375,000"
                value={askingPrice}
                onChange={(e) => setAskingPrice(e.target.value)}
                className="pd-input"
                data-testid="pd-price"
              />
            </div>

            <div className="pd-row">
              <Label className="pd-label">Key features</Label>
              <p className="pd-hint">Tap to add. The AI will only mention what you select — never invented.</p>
              <div className="pd-feature-chips">
                {FEATURE_CHIPS.map(({ label, icon: Icon }) => (
                  <button
                    key={label}
                    type="button"
                    className={`pd-feature ${features.includes(label) ? "is-on" : ""}`}
                    onClick={() => toggleFeature(label)}
                    data-testid={`pd-feature-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                  >
                    <Icon size={13} /> {label}
                  </button>
                ))}
              </div>
              <div className="pd-custom-feature">
                <Input
                  placeholder="Add your own feature…"
                  value={customFeature}
                  onChange={(e) => setCustomFeature(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomFeature(); } }}
                  className="pd-input"
                  data-testid="pd-custom-feature-input"
                />
                <button type="button" className="pd-add-btn" onClick={addCustomFeature} disabled={!customFeature.trim()} data-testid="pd-custom-feature-add">
                  <Plus size={15} /> Add
                </button>
              </div>
              {features.length > 0 && (
                <div className="pd-selected-features">
                  {features.map((f) => (
                    <span key={f} className="pd-selected-chip" data-testid={`pd-selected-${f.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                      {f}
                      <button type="button" onClick={() => removeFeature(f)} aria-label={`Remove ${f}`}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="pd-row">
              <Label className="pd-label"><MapPin size={14} /> Location notes (only what's verified)</Label>
              <Textarea
                rows={2}
                placeholder="e.g. 8-min walk to the village shops &amp; pub. (Don't include catchment / commute times unless you can evidence them.)"
                value={locationNotes}
                onChange={(e) => setLocationNotes(e.target.value)}
                className="pd-textarea"
                data-testid="pd-location-notes"
              />
            </div>

            <div className="pd-row">
              <Label className="pd-label">Anything else?</Label>
              <Textarea
                rows={2}
                placeholder="Anything the AI should know but might not infer — chain status, special history, recent works, etc."
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                className="pd-textarea"
                data-testid="pd-additional-notes"
              />
            </div>
          </div>

          {/* ─── RIGHT: STYLE + RESULT ─────────────────────────────── */}
          <div className="pd-right">
            <div className="pd-card pd-style">
              <div className="pd-section-head">
                <span className="pd-section-num">2</span>
                <h2>Style &amp; length</h2>
              </div>

              <div className="pd-style-block">
                <Label className="pd-label">Length</Label>
                <div className="pd-toggle-group" data-testid="pd-length-toggle">
                  {LENGTHS.map((l) => (
                    <button
                      key={l.key}
                      type="button"
                      className={`pd-toggle ${length === l.key ? "is-active" : ""}`}
                      onClick={() => setLength(l.key)}
                      data-testid={`pd-length-${l.key}`}
                    >
                      <span className="pd-toggle-main">{l.label}</span>
                      <span className="pd-toggle-hint">{l.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="pd-style-block">
                <Label className="pd-label">Angle</Label>
                <div className="pd-toggle-group" data-testid="pd-variant-toggle">
                  {VARIANTS.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      className={`pd-toggle ${variant === v.key ? "is-active" : ""}`}
                      onClick={() => setVariant(v.key)}
                      data-testid={`pd-variant-${v.key}`}
                    >
                      <span className="pd-toggle-main">{v.label}</span>
                      <span className="pd-toggle-hint">{v.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              {isSuperAdmin && branches.length > 0 && (
                <div className="pd-style-block">
                  <Label className="pd-label">Preview as branch (admin only)</Label>
                  <select
                    className="pd-input"
                    value={previewBranchId}
                    onChange={(e) => setPreviewBranchId(e.target.value)}
                    data-testid="pd-preview-branch"
                  >
                    <option value="">(Defaults — no branch personality)</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                  <p className="pd-hint" style={{ marginTop: 6 }}>
                    Renders with the selected branch's brand voice + rules so you can QA any tone without switching accounts.
                  </p>
                </div>
              )}

              <button
                type="button"
                className="pd-generate"
                onClick={generate}
                disabled={generating}
                data-testid="pd-generate-btn"
              >
                {generating
                  ? (<><Loader2 size={16} className="pd-spin" /> Writing your description…</>)
                  : (<><Sparkles size={16} /> Write description</>)}
              </button>

              <p className="pd-disclaimer">
                Uses your branch's brand tone &amp; rules from Admin → Branch Personality.
                CPR/BPR guardrails are always on.
              </p>
            </div>

            {result && (
              <div className="pd-card pd-result" data-testid="pd-result">
                <div className="pd-result-head">
                  <h2><Sparkles size={16} /> Your description</h2>
                  <button type="button" className="pd-copy-all" onClick={copyFull} data-testid="pd-copy-all">
                    {copiedKey === "full" ? (<><ClipboardCheck size={14} /> Copied</>) : (<><Copy size={14} /> Copy all</>)}
                  </button>
                </div>

                {result.compliance_notes?.length > 0 && (
                  <div className="pd-compliance-warn" data-testid="pd-compliance-warn">
                    <div className="pd-compliance-warn-head">
                      <AlertTriangle size={15} /> CPR/BPR review points
                    </div>
                    <ul>
                      {result.compliance_notes.map((n, i) => (
                        <li key={i}>{n}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.compliance_notes?.length === 0 && (
                  <div className="pd-compliance-clean">
                    <ShieldCheck size={15} /> Clean — no CPR/BPR flags raised
                  </div>
                )}

                {result.hook && (
                  <div className="pd-block">
                    <div className="pd-block-head">
                      <span>Hook</span>
                      <button type="button" onClick={() => copyText("hook", result.hook)} className="pd-mini-copy" data-testid="pd-copy-hook">
                        {copiedKey === "hook" ? <ClipboardCheck size={12} /> : <Copy size={12} />}
                      </button>
                    </div>
                    <p className="pd-hook" data-testid="pd-hook">{result.hook}</p>
                  </div>
                )}

                {result.bullets?.length > 0 && (
                  <div className="pd-block">
                    <div className="pd-block-head">
                      <span>Bullets</span>
                      <button
                        type="button"
                        onClick={() => copyText("bullets", result.bullets.map((b) => `• ${b}`).join("\n"))}
                        className="pd-mini-copy"
                        data-testid="pd-copy-bullets"
                      >
                        {copiedKey === "bullets" ? <ClipboardCheck size={12} /> : <Copy size={12} />}
                      </button>
                    </div>
                    <ul className="pd-bullets" data-testid="pd-bullets">
                      {result.bullets.map((b, i) => (<li key={i}>{b}</li>))}
                    </ul>
                  </div>
                )}

                {result.body && (
                  <div className="pd-block">
                    <div className="pd-block-head">
                      <span>Description</span>
                      <button type="button" onClick={() => copyText("body", result.body)} className="pd-mini-copy" data-testid="pd-copy-body">
                        {copiedKey === "body" ? <ClipboardCheck size={12} /> : <Copy size={12} />}
                      </button>
                    </div>
                    <div className="pd-body" data-testid="pd-body">
                      {result.body.split(/\n\n+/).map((para, i) => (<p key={i}>{para.trim()}</p>))}
                    </div>
                  </div>
                )}

                {result.summary && (
                  <div className="pd-block">
                    <div className="pd-block-head">
                      <span>Summary (card preview)</span>
                      <button type="button" onClick={() => copyText("summary", result.summary)} className="pd-mini-copy" data-testid="pd-copy-summary">
                        {copiedKey === "summary" ? <ClipboardCheck size={12} /> : <Copy size={12} />}
                      </button>
                    </div>
                    <p className="pd-summary" data-testid="pd-summary">{result.summary}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
